import { useEffect, useMemo, useState } from 'react'

/**
 * Saisie d'un numero de telephone — indicatif dans une LISTE DEROULANTE, facon
 * WhatsApp (demande d'Enzo, S32).
 *
 * POURQUOI PAS LA PASTILLE FIGEE « 🇲🇶 +596 » DU PROTOTYPE :
 * la refonte de la connexion (v0.12.0, 6 mai) a ete declenchee par un INCIDENT —
 * un exposant qui avait oublie son PIN s'etait recree un compte, et le format
 * de telephone libre (`0696…`, `+596…`, `+33…`) multipliait les variantes d'un
 * meme numero en base. La reponse a ete la normalisation **E.164** cote backend
 * (`common/utils/phone.utils.ts`), avec **MQ *et* FR** parmi les pays acceptes.
 * Un indicatif +596 code en dur contredirait cette decision : il rendrait la
 * saisie impossible aux numeros metropolitains, qui existent deja en base.
 *
 * CONTRAT : la valeur remontee est TOUJOURS en E.164 (`+596696123456`) — donc
 * deja normalisee quand elle arrive au backend. Le zero initial d'un numero
 * local (`0696…`) est retire automatiquement : c'est le geste que fait WhatsApp,
 * et sans lui on produirait `+5960696…`, invalide.
 *
 * La liste reste volontairement courte — les territoires reellement servis, plus
 * « Autre pays » pour saisir un numero international complet. La validation
 * finale appartient au backend (`normalizePhone`, mobiles uniquement).
 */

export const PAYS_TELEPHONE = [
  { code: 'MQ', drapeau: '🇲🇶', nom: 'Martinique', indicatif: '+596', exemple: '696 12 34 56' },
  { code: 'GP', drapeau: '🇬🇵', nom: 'Guadeloupe', indicatif: '+590', exemple: '690 12 34 56' },
  { code: 'GF', drapeau: '🇬🇫', nom: 'Guyane', indicatif: '+594', exemple: '694 12 34 56' },
  { code: 'FR', drapeau: '🇫🇷', nom: 'France', indicatif: '+33', exemple: '6 12 34 56 78' },
  { code: 'AUTRE', drapeau: '🌍', nom: 'Autre pays', indicatif: '', exemple: '+1 758 123 4567' },
]

const AUTRE = PAYS_TELEPHONE[PAYS_TELEPHONE.length - 1]

/** Decoupe une valeur E.164 en (pays, partie locale) pour l'affichage. */
export function decouperTelephone(valeur) {
  const v = String(valeur || '').replace(/[^\d+]/g, '')
  if (!v.startsWith('+')) {
    // Ancien format local (`0696…`) : on l'attribue au pays par defaut.
    return { pays: PAYS_TELEPHONE[0], local: v.replace(/^0+/, '') }
  }
  // Indicatif le plus long d'abord : +596 avant +59, etc.
  const trouve = [...PAYS_TELEPHONE]
    .filter((p) => p.indicatif)
    .sort((a, b) => b.indicatif.length - a.indicatif.length)
    .find((p) => v.startsWith(p.indicatif))
  if (!trouve) return { pays: AUTRE, local: v }
  return { pays: trouve, local: v.slice(trouve.indicatif.length) }
}

/** Recompose l'E.164 a partir du pays choisi et de la saisie locale. */
function composerTelephone(pays, saisie) {
  if (pays.code === 'AUTRE') {
    const v = String(saisie).replace(/[^\d+]/g, '')
    return v.startsWith('+') ? v : v ? `+${v.replace(/^0+/, '')}` : ''
  }
  // Le zero initial d'un numero local ne fait pas partie du format international.
  const chiffres = String(saisie).replace(/\D/g, '').replace(/^0+/, '')
  return chiffres ? `${pays.indicatif}${chiffres}` : ''
}

function TelephoneInput({
  value, onChange, t, id = 'telephone', autoFocus = false, disabled = false, label,
}) {
  // Le pays choisi vit dans un ETAT, il n'est PAS deduit de la valeur.
  //
  // Bug corrige en S32 (remonte par Enzo) : le pays etait relu depuis `value` a
  // chaque rendu. Choisir « 🇫🇷 +33 » sur un champ VIDE produisait une valeur
  // vide (il n'y a pas d'E.164 sans numero), donc la relecture ne trouvait plus
  // d'indicatif et retombait sur le pays par defaut. Le select revenait a
  // « 🇲🇶 », l'exemple du champ ne changeait pas — et le numero saisi ensuite
  // partait avec le mauvais indicatif (`+596` sur un numero metropolitain),
  // d'ou le « numero non reconnu ».
  const [codePays, setCodePays] = useState(() => decouperTelephone(value).pays.code)
  const pays = PAYS_TELEPHONE.find((p) => p.code === codePays) || PAYS_TELEPHONE[0]

  // Une valeur posee de l'EXTERIEUR (numero repris de l'etape precedente, PIN
  // oublie pre-rempli...) impose son indicatif. La saisie de l'utilisateur, elle,
  // ne change jamais le pays : il l'a choisi.
  useEffect(() => {
    const v = String(value || '')
    if (!v.startsWith('+')) return
    const detecte = decouperTelephone(v).pays.code
    if (detecte !== codePays) setCodePays(detecte)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Partie locale affichee : ce qui reste apres l'indicatif du pays CHOISI.
  const local = useMemo(() => {
    const v = String(value || '').replace(/[^\d+]/g, '')
    if (!v) return ''
    if (pays.code === 'AUTRE') return v
    if (v.startsWith(pays.indicatif)) return v.slice(pays.indicatif.length)
    return decouperTelephone(v).local
  }, [value, pays])

  const changerPays = (code) => {
    const nouveau = PAYS_TELEPHONE.find((p) => p.code === code) || PAYS_TELEPHONE[0]
    setCodePays(nouveau.code)
    onChange(composerTelephone(nouveau, local))
  }

  const champCls = `${t.inputBg} border ${t.inputBorder} ${t.inputText} outline-none focus:border-primary transition`

  return (
    <div>
      {label && (
        <label htmlFor={id} className={`block text-xs font-medium ${t.textSecondary} mb-1.5`}>{label}</label>
      )}
      <div className="flex items-center gap-2">
        {/* Indicatif — <select> natif : c'est la liste deroulante du systeme,
            donc le geste attendu sur telephone (et accessible au clavier). */}
        <div className="relative flex-shrink-0">
          <select
            aria-label="Indicatif du pays"
            value={pays.code}
            onChange={(e) => changerPays(e.target.value)}
            disabled={disabled}
            className={`appearance-none rounded-2xl pl-3.5 pr-7 py-4 text-[15px] font-bold cursor-pointer ${champCls}`}
          >
            {PAYS_TELEPHONE.map((p) => (
              <option key={p.code} value={p.code}>
                {p.drapeau} {p.indicatif || p.nom}
              </option>
            ))}
          </select>
          <span aria-hidden="true"
            className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[9px] ${t.textMuted}`}>▾</span>
        </div>

        <input
          id={id}
          type="tel"
          inputMode={pays.code === 'AUTRE' ? 'text' : 'numeric'}
          value={local}
          onChange={(e) => onChange(composerTelephone(pays, e.target.value))}
          placeholder={pays.exemple}
          autoFocus={autoFocus}
          disabled={disabled}
          className={`flex-1 min-w-0 px-4 py-4 text-[16px] font-semibold rounded-2xl ${champCls}`}
        />
      </div>
      <p className={`text-[11px] mt-1.5 ${t.textMuted}`}>
        {pays.code === 'AUTRE'
          ? 'Saisissez le numéro complet avec son indicatif.'
          : `${pays.nom} — inutile de taper le 0 du début.`}
      </p>
    </div>
  )
}

export default TelephoneInput
